import React from "react";
import { motion } from "framer-motion";
import { UserPlus, Award, Briefcase, DollarSign } from "lucide-react";

const HowItWorksAlt: React.FC = () => {
  const steps = [
    {
      number: "01",
      icon: UserPlus,
      title: "Join Our Community",
      description:
        "Create your profile and add your skills, languages, and expertise to match with the right opportunities.",
      color: "#8B52FF",
    },
    {
      number: "02",
      icon: Award,
      title: "Get Certified",
      description:
        "Validate your expertise with specialized AI certifications designed for your field of knowledge.",
      color: "#9D5CFF",
    },
    {
      number: "03",
      icon: Briefcase,
      title: "Match with Projects",
      description:
        "Explore and select from thousands of AI projects that match your skills and interests.",
      color: "#C75A70",
    },
    {
      number: "04",
      icon: DollarSign,
      title: "Start Earning",
      description:
        "Complete tasks on your schedule and receive competitive compensation for your expertise.",
      color: "#E05A6A",
    },
  ];

  return (
    <section className="relative py-20 bg-[#FAFAFA] overflow-hidden">
      {/* Decorative background pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(#8B52FF 1px, transparent 1px)`,
          backgroundSize: "30px 30px",
        }}
      />

      {/* Gradient orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8B52FF]/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E05A6A]/10 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 lg:mb-1"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-sm font-medium text-[#8B52FF] mb-4"
          >
            Simple Process
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            How It{" "}
            <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
              Works
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Start your journey in 4 simple steps and begin contributing to the
            future of AI
          </p>
        </motion.div>

        {/* Steps - Desktop Timeline */}
        <div className="hidden lg:block relative">
          {/* Connecting line */}
          <div className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#8B52FF]/20 via-[#A855F7]/20 to-[#E05A6A]/20" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#8B52FF] via-[#A855F7] to-[#E05A6A] origin-left"
          />

          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative pt-16"
              >
                {/* Timeline dot */}
                <div className="absolute top-[88px] left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.5 + index * 0.15 }}
                    className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center border-4"
                    style={{ borderColor: step.color }}
                  >
                    <span
                      className="text-sm font-bold"
                      style={{ color: step.color }}
                    >
                      {step.number}
                    </span>
                  </motion.div>
                </div>

                {/* Card */}
                <div className="group mt-8 p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full">
                  {/* Icon */}
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${step.color}20, ${step.color}10)`,
                    }}
                  >
                    <step.icon
                      className="w-7 h-7"
                      style={{ color: step.color }}
                    />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Steps - Mobile/Tablet */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex gap-4"
            >
              {/* Left side - Number & Line */}
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                  }}
                >
                  {step.number}
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 flex-1 my-2 bg-gradient-to-b from-[#8B52FF]/30 to-[#E05A6A]/30 rounded-full" />
                )}
              </div>

              {/* Right side - Content */}
              <div className="flex-1 pb-8">
                <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${step.color}20, ${step.color}10)`,
                      }}
                    >
                      <step.icon
                        className="w-5 h-5"
                        style={{ color: step.color }}
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-14 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-2 rounded-2xl bg-white shadow-lg border border-gray-100">
            <p className="text-gray-600 px-4">
              Ready to shape the future of AI?
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group px-6 py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-xl text-white font-semibold shadow-lg shadow-[#8B52FF]/20 hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              Get Started
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default HowItWorksAlt;
